import { ApiError, isRecord } from '@/lib/api'

import type { AlertCatalog, AlertCatalogField, AlertFieldType } from '../../api/alert-rules-api'

export type GroupOperator = 'and' | 'or'
export type ConditionNode = { kind: 'condition'; key: string; field: string; op: string; value: string | string[] }
export type GroupNode = { kind: 'group'; key: string; op: GroupOperator; children: BuilderNode[] }
export type BuilderNode = ConditionNode | GroupNode

export type BuilderCatalog = {
  schemaVersion: number; catalogVersion: string; evaluatorVersion: string; assetType: string; event: string
  fields: AlertCatalogField[]; groupOperators: GroupOperator[]; builderDepth: number
}

export const OPERATOR_PHRASES: Record<string, string> = {
  eq: 'is', ne: 'is not', lt: 'is below', lte: 'is at or below', gt: 'is above', gte: 'is at or above',
  in: 'is any of', startsWith: 'starts with', endsWith: 'ends with', contains: 'contains',
}

// Identifiers the platform assigns; the builder never offers them (printer-limited rules set asset.id themselves).
const INTERNAL_FIELDS = new Set(['asset.id', 'supply.positionId', 'supply.installationId', 'supply.instance'])
const EXPRESSION_PATH = '/when/predicate/expression'
const UNNESTED_BUILDER_DEPTH = 2
const GROUP_KEYS = ['op', 'args']
const CONDITION_KEYS = ['op', 'field', 'value']
const DECIMAL = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/
const WHOLE_NUMBER = /^-?\d+$/
const VALUE_PROBLEMS: Record<AlertFieldType, string> = { string: 'Enter a value.', double: 'Enter a number.', int: 'Enter a whole number.' }

export function builderCatalog(catalog: AlertCatalog): BuilderCatalog | null {
  const assetType = catalog.assetTypes[0]
  const event = catalog.events[0]
  const groupOperators = (['and', 'or'] as const).filter((op) => catalog.booleanOperators.includes(op))
  if (!assetType || !event || !catalog.predicateLanguages.includes('builder') || !groupOperators.includes('and')) return null
  const fields = catalog.fields
    .filter((field) => !INTERNAL_FIELDS.has(field.id) && field.events.includes(event))
    .map((field) => ({ ...field, operators: field.operators.filter((op) => op in OPERATOR_PHRASES) }))
    .filter((field) => field.operators.length > 0)
  if (!fields.length) return null
  return {
    schemaVersion: catalog.schemaVersion, catalogVersion: catalog.catalogVersion, evaluatorVersion: catalog.evaluatorVersion,
    assetType, event, fields, groupOperators, builderDepth: catalog.builderDepth ?? UNNESTED_BUILDER_DEPTH,
  }
}

// The server counts the root as depth 1 and rejects any node deeper than builderDepth; a group's
// conditions sit two levels below the group's own zero-based depth here.
export function canNestGroupAt(catalog: BuilderCatalog, depth: number) {
  return depth + 2 <= catalog.builderDepth
}

export function newKey() {
  return crypto.randomUUID()
}

function emptyValue(field: AlertCatalogField, op: string): string | string[] {
  return op === 'in' && field.allowedValues.length ? [] : ''
}

export function newCondition(catalog: BuilderCatalog): ConditionNode {
  const field = catalog.fields[0]!
  const op = field.operators[0]!
  return { kind: 'condition', key: newKey(), field: field.id, op, value: emptyValue(field, op) }
}

export function newGroup(catalog: BuilderCatalog, op: GroupOperator, children: BuilderNode[] = [newCondition(catalog)]): GroupNode {
  return { kind: 'group', key: newKey(), op, children }
}

export function fieldOf(catalog: BuilderCatalog, id: string) {
  return catalog.fields.find((field) => field.id === id)
}

export function withField(catalog: BuilderCatalog, condition: ConditionNode, fieldId: string): ConditionNode {
  const field = fieldOf(catalog, fieldId)
  if (!field) return condition
  const op = field.operators.includes(condition.op) ? condition.op : field.operators[0]!
  return { ...condition, field: field.id, op, value: emptyValue(field, op) }
}

export function withOperator(catalog: BuilderCatalog, condition: ConditionNode, op: string): ConditionNode {
  const field = fieldOf(catalog, condition.field)
  if (!field || (op === 'in') === (condition.op === 'in')) return { ...condition, op }
  if (!field.allowedValues.length) {
    const value = condition.op === 'in' && typeof condition.value === 'string' ? condition.value.split(',')[0]!.trim() : condition.value
    return { ...condition, op, value }
  }
  const value = Array.isArray(condition.value) ? condition.value[0] ?? '' : condition.value ? [condition.value] : []
  return { ...condition, op, value }
}

export function lowSupplyTemplate(catalog: BuilderCatalog): GroupNode | null {
  const percent = fieldOf(catalog, 'supply.percentRemaining')
  const type = fieldOf(catalog, 'supply.type')
  const types = ['toner', 'ink']
  if (!percent?.operators.includes('lte') || !type?.operators.includes('in')
    || (type.allowedValues.length && !types.every((value) => type.allowedValues.includes(value)))) return null
  return newGroup(catalog, 'and', [
    { kind: 'condition', key: newKey(), field: percent.id, op: 'lte', value: '25' },
    { kind: 'condition', key: newKey(), field: type.id, op: 'in', value: type.allowedValues.length ? types : types.join(', ') },
  ])
}

export function parseBuilderDocument(catalog: BuilderCatalog, document: unknown): GroupNode | null {
  if (!isRecord(document) || document.schemaVersion !== catalog.schemaVersion || document.catalogVersion !== catalog.catalogVersion
    || document.evaluatorVersion !== catalog.evaluatorVersion || document.assetType !== catalog.assetType) return null
  const when = document.when
  if (!isRecord(when) || when.event !== catalog.event || !isRecord(when.predicate) || when.predicate.language !== 'builder') return null
  const root = parseNode(catalog, when.predicate.expression, 0)
  if (!root) return null
  return root.kind === 'group' ? root : newGroup(catalog, 'and', [root])
}

function parseNode(catalog: BuilderCatalog, raw: unknown, depth: number): BuilderNode | null {
  if (!isRecord(raw) || typeof raw.op !== 'string') return null
  if (raw.op === 'and' || raw.op === 'or') {
    if (!catalog.groupOperators.includes(raw.op) || !canNestGroupAt(catalog, depth)
      || !Array.isArray(raw.args) || raw.args.length < 2 || !hasOnlyKeys(raw, GROUP_KEYS)) return null
    const children = raw.args.map((arg) => parseNode(catalog, arg, depth + 1))
    return children.every((child) => child !== null) ? newGroup(catalog, raw.op, children) : null
  }
  const field = typeof raw.field === 'string' ? fieldOf(catalog, raw.field) : undefined
  if (!field || !field.operators.includes(raw.op) || !hasOnlyKeys(raw, CONDITION_KEYS)) return null
  const value = parseValue(field, raw.op, raw.value)
  return value === null ? null : { kind: 'condition', key: newKey(), field: field.id, op: raw.op, value }
}

function hasOnlyKeys(raw: Record<string, unknown>, keys: string[]) {
  return Object.keys(raw).every((key) => keys.includes(key))
}

function parseValue(field: AlertCatalogField, op: string, raw: unknown): string | string[] | null {
  const scalar = (item: unknown): string | null => {
    if (field.type === 'string' ? typeof item !== 'string' : typeof item !== 'number' || !Number.isFinite(item)) return null
    const text = String(item)
    if (!text || text.trim() !== text || (field.type !== 'string' && !DECIMAL.test(text))) return null
    return field.allowedValues.length && !field.allowedValues.includes(text) ? null : text
  }
  if (op !== 'in') return scalar(raw)
  if (!Array.isArray(raw) || !raw.length) return null
  const items = raw.map(scalar)
  if (items.some((item) => item === null)) return null
  const values = items as string[]
  if (field.allowedValues.length) return values
  return values.some((item) => item.includes(',')) ? null : values.join(', ')
}

export type SerializedBuilder = { document: unknown; paths: Map<string, string>; problems: Map<string, string> }

export function serializeBuilder(catalog: BuilderCatalog, root: GroupNode): SerializedBuilder {
  const paths = new Map<string, string>()
  const problems = new Map<string, string>()
  const expression = serializeNode(catalog, root, EXPRESSION_PATH, paths, problems)
  const document = {
    schemaVersion: catalog.schemaVersion, catalogVersion: catalog.catalogVersion, evaluatorVersion: catalog.evaluatorVersion,
    assetType: catalog.assetType, when: { event: catalog.event, predicate: { language: 'builder', expression } },
  }
  return { document, paths, problems }
}

function serializeNode(catalog: BuilderCatalog, node: BuilderNode, path: string, paths: Map<string, string>, problems: Map<string, string>): unknown {
  if (node.kind === 'group') {
    if (!node.children.length) {
      problems.set(node.key, 'Add a condition to this group.')
      return null
    }
    if (node.children.length === 1) return serializeNode(catalog, node.children[0]!, path, paths, problems)
    paths.set(path, node.key)
    return { op: node.op, args: node.children.map((child, index) => serializeNode(catalog, child, `${path}/args/${index}`, paths, problems)) }
  }
  paths.set(path, node.key)
  const field = fieldOf(catalog, node.field)
  const value = field ? serializeValue(field, node) : null
  if (value === null) problems.set(node.key, VALUE_PROBLEMS[field?.type ?? 'string'])
  return { op: node.op, field: node.field, value }
}

function serializeValue(field: AlertCatalogField, node: ConditionNode): unknown {
  const typed = (text: string) => {
    if (field.type === 'string') return text
    return (field.type === 'int' ? WHOLE_NUMBER : DECIMAL).test(text) ? Number(text) : null
  }
  if (Array.isArray(node.value)) return node.value.length ? node.value.map(typed) : null
  if (node.op !== 'in') {
    const text = node.value.trim()
    return text ? typed(text) : null
  }
  const items = node.value.split(',').map((item) => item.trim()).filter(Boolean).map(typed)
  return items.length && items.every((item) => item !== null) ? items : null
}

export type ValidationProblem = { message: string; nodeKey: string | null }

export function validationProblem(error: unknown, paths: Map<string, string>): ValidationProblem | null {
  if (!(error instanceof ApiError) || error.status !== 400 || error.code !== 'INVALID_ALERT_PREDICATE') return null
  let location: unknown
  try {
    const body: unknown = JSON.parse(error.body)
    location = isRecord(body) && isRecord(body.validationLocation) ? body.validationLocation.path : undefined
  } catch {
    location = undefined
  }
  return { message: error.message, nodeKey: typeof location === 'string' ? nodeAt(paths, location) : null }
}

function nodeAt(paths: Map<string, string>, location: string): string | null {
  let best: string | null = null
  let bestLength = -1
  for (const [path, key] of paths) {
    if ((location === path || location.startsWith(`${path}/`)) && path.length > bestLength) {
      best = key
      bestLength = path.length
    }
  }
  return best
}

export function describeCondition(catalog: BuilderCatalog, node: ConditionNode): string {
  const field = fieldOf(catalog, node.field)
  const values = Array.isArray(node.value) ? node.value.join(', ') : node.value.trim()
  const unit = field && isPercent(field) ? ' percent' : ''
  return `${field?.label ?? node.field} ${OPERATOR_PHRASES[node.op] ?? node.op} ${values || '…'}${unit}`
}

export function isPercent(field: AlertCatalogField) {
  return field.unit.startsWith('percent')
}

export function updateNode(root: GroupNode, key: string, update: (node: BuilderNode) => BuilderNode | null): GroupNode {
  const visit = (node: BuilderNode): BuilderNode | null => {
    if (node.key === key) return update(node)
    if (node.kind === 'condition') return node
    const children = node.children.map(visit).filter((child) => child !== null)
    return children.length === node.children.length && children.every((child, index) => child === node.children[index])
      ? node : { ...node, children }
  }
  return (visit(root) as GroupNode | null) ?? root
}
